import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, isDevMode } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ApprovalToastHost } from './components/approval-toast-host/approval-toast-host';
import { ChangelogDialog } from './components/changelog-dialog/changelog-dialog';
import { MeetYourTeamDialog } from './components/meet-your-team-dialog/meet-your-team-dialog';
import { EmailReviewDialogHost } from './components/email-review-dialog-host/email-review-dialog-host';
import { EnvGate } from './components/env-gate/env-gate';
import { ErrorModal } from './components/error-modal/error-modal';
import { MembershipInactiveDialog } from './components/membership-inactive-dialog/membership-inactive-dialog';
import { TcAcceptanceDialog } from './components/tc-acceptance-dialog/tc-acceptance-dialog';
import { CannyService } from './services/canny-service';
import { FullStoryService } from './services/fullstory.service';
import { PartnerstackAttributionService } from './services/partnerstack-attribution.service';
import { ReachOutNotificationsService } from './services/reach-out-notifications.service';
import { ExtraUsageRequestNotificationsService } from './services/extra-usage-request-notifications.service';
import { MemberConnectorNotificationsService } from './services/member-connector-notifications.service';
import { TimezoneDetectionService } from './services/timezone-detection.service';
import { ImpersonationBanner } from './components/impersonation-banner/impersonation-banner';
import { OfflineBanner } from './components/offline-banner/offline-banner';
import { UpdateBanner } from './components/update-banner/update-banner';
import { FeatureFlagsService } from './services/feature-flags.service';
import { PushSubscriptionService } from './services/push-subscription.service';
import { SwUpdateService } from './services/sw-update.service';
import { ConnectionHealthService } from './services/connection-health.service';
import { SessionRedirectService } from './services/session-redirect.service';
import { AppStateService } from './services/app-state.service';
import { ViewportService } from './services/viewport.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-root',
    imports: [
        RouterOutlet,
        MembershipInactiveDialog,
        TcAcceptanceDialog,
        ChangelogDialog,

        MeetYourTeamDialog,
        EmailReviewDialogHost,
        ErrorModal,
        ImpersonationBanner,
        OfflineBanner,
        UpdateBanner,
        ApprovalToastHost,
        EnvGate,
    ],
    templateUrl: './app.html',
    styleUrl: './app.css',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class App {
    // Injecting this just ensures it runs after login
    // noinspection JSUnusedGlobalSymbols
    cannyService = inject(CannyService);
    // noinspection JSUnusedGlobalSymbols
    fullStoryService = inject(FullStoryService);
    // noinspection JSUnusedGlobalSymbols
    timezoneDetection = inject(TimezoneDetectionService);
    // Eager so DDR browser reach-outs ping on any route, not just chat pages.
    // noinspection JSUnusedGlobalSymbols
    reachOutNotifications = inject(ReachOutNotificationsService);
    extraUsageRequestNotifications = inject(ExtraUsageRequestNotificationsService);
    memberConnectorNotifications = inject(MemberConnectorNotificationsService);
    protected readonly appState = inject(AppStateService);
    private readonly viewport = inject(ViewportService);
    private readonly partnerstackAttribution = inject(PartnerstackAttributionService);
    private readonly featureFlags = inject(FeatureFlagsService);
    private readonly pushService = inject(PushSubscriptionService);
    private readonly swUpdate = inject(SwUpdateService);
    // Eager so a broken Velma/ArrowStream grant toasts on any route, not
    // only when the Connections panel is open. The service self-starts from
    // auth; this inject is what keeps it alive.
    // noinspection JSUnusedLocalSymbols
    private readonly connectionHealth = inject(ConnectionHealthService);
    // Injected only so its effect runs: forces anonymous users off protected pages.
    // noinspection JSUnusedLocalSymbols
    private readonly sessionRedirect = inject(SessionRedirectService);
    readonly toastPlacement = computed(() => (this.viewport.isMobileDimension() ? 'bottom-end' : 'top-end'));
    readonly pwaEnabled = this.featureFlags.pwaEnabled;

    constructor() {
        const searchParams = new URLSearchParams(window.location.search);
        this.partnerstackAttribution.captureClickId(searchParams.get('ps_xid'));

        // SW registration lives in app.config (provideServiceWorker) - it's
        // the update vehicle that gets long-lived tabs off stale bundles, so
        // it is NOT gated by the pwa-enabled flag. The flag only gates the
        // PWA install surface: manifest link + push subscription.
        this.swUpdate.init();

        effect(() => {
            const enabled = this.pwaEnabled();
            if (!('serviceWorker' in navigator) || isDevMode()) {
                return;
            }

            if (enabled) {
                this.enablePwa();
            } else {
                this.disablePwa();
            }
        });
    }

    private enablePwa() {
        const manifestLink = document.head.querySelector('link[rel="manifest"]');
        if (!manifestLink) {
            const link = document.createElement('link');
            link.rel = 'manifest';
            link.href = 'manifest.webmanifest';
            document.head.appendChild(link);
        }

        // Check for existing push subscription (user previously enabled notifications)
        this.pushService.checkExistingSubscription();
    }

    private disablePwa() {
        document.head.querySelector('link[rel="manifest"]')?.remove();
    }
}
